import React, { useState, useRef, useReducer } from 'react'
import { Canvas } from '@react-three/fiber'
import { LabelLayer } from './LabelLayer'
import LabelHUD from './LabelHUD'
import { newBox, boxReducer, BoxAction } from './boxReducer'
import { exportJSON, importJSON } from './io'

type Mode = 'idle' | 'create' | 'translate' | 'rotate' | 'scale'

const LabelLayerTest: React.FC = () => {
  const [boxes, dispatch] = useReducer(boxReducer, [
    newBox({
      frameId: 0,
      cx: 0,
      cy: 0,
      cz: 0,
      lx: 2,
      ly: 1,
      lz: 1,
      cls: 'person'
    }),
    newBox({
      frameId: 0,
      cx: 3,
      cy: 0,
      cz: 0,
      lx: 1,
      ly: 1,
      lz: 2,
      cls: 'vehicle'
    }),
    newBox({
      frameId: 1,
      cx: 0,
      cy: 2,
      cz: 0,
      lx: 1,
      ly: 1,
      lz: 1,
      cls: 'bag'
    })
  ])

  const [currentFrame, setCurrentFrame] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [mode, setMode] = useState<Mode>('idle')
  const [selectedId, setSelectedId] = useState<string|undefined>()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Filter boxes for current frame
  const frameBoxes = boxes.filter(box => box.frameId === currentFrame)

  const handleTransformStart = () => {
    setIsDragging(true)
  }

  const handleTransformEnd = () => {
    setIsDragging(false)
  }

  const handleBoxAction = (action: BoxAction) => {
    dispatch(action)
  }

  const toggleCreateMode = () => {
    setMode(mode === 'create' ? 'idle' : 'create')
  }

  const handleModeChange = (newMode: 'idle' | 'create' | 'translate' | 'rotate' | 'scale') => {
    setMode(newMode)
  }

  const handleSelectionChange = (id?: string) => {
    setSelectedId(id)
  }

  const handleExport = () => {
    exportJSON(boxes)
  }

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      importJSON(file)
        .then((importedBoxes) => {
          dispatch({
            type: 'bulkSet',
            boxes: importedBoxes
          })
          // Clear the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        })
        .catch((error) => {
          alert(`Import failed: ${error.message}`)
          // Clear the file input
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        })
    }
  }

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all boxes?')) {
      dispatch({
        type: 'bulkSet',
        boxes: []
      })
      setSelectedId(undefined)
    }
  }

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <div style={{ padding: '20px', position: 'absolute', top: 0, left: 0, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.8)', color: 'white' }}>
        <h2>LabelLayer Test</h2>
        <p>Current Frame: {currentFrame}</p>
        <button onClick={() => setCurrentFrame(0)}>Frame 0</button>
        <button onClick={() => setCurrentFrame(1)}>Frame 1</button>
        <p>Boxes in current frame: {frameBoxes.length}</p>
        <p>Dragging: {isDragging ? 'Yes' : 'No'}</p>
        <p>Mode: {mode}</p>
        <p>Selected: {selectedId ? 'Yes' : 'No'}</p>
        <button 
          onClick={toggleCreateMode}
          style={{ 
            backgroundColor: mode === 'create' ? '#ff6b35' : '#4a4a4a',
            color: 'white',
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          {mode === 'create' ? 'Exit Create Mode' : 'Create Mode'}
        </button>

        {/* Import/Export Buttons */}
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button
            onClick={handleExport}
            style={{
              backgroundColor: '#4CAF50',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Export JSON
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            style={{ display: 'none' }}
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              backgroundColor: '#2196F3',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Import JSON
          </button>
          
          <button
            onClick={handleClear}
            style={{
              backgroundColor: '#f44336',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Clear All
          </button>
        </div>
        
        {/* Keyboard Shortcuts */}
        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#1a1a1a', borderRadius: '4px', fontSize: '12px' }}>
          <p><strong>Keyboard Shortcuts:</strong></p>
          <ul style={{ marginLeft: '20px', fontSize: '11px' }}>
            <li><strong>B</strong> - Toggle create mode</li>
            <li><strong>W</strong> - Translate mode</li>
            <li><strong>E</strong> - Rotate mode</li>
            <li><strong>R</strong> - Scale mode</li>
            <li><strong>Delete/Backspace</strong> - Delete selected box</li>
            <li><strong>L</strong> - Toggle lock on selected box</li>
          </ul>
        </div>
        {mode === 'create' && (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '4px' }}>
            <p><strong>Create Mode Instructions:</strong></p>
            <p><strong>Step 1:</strong> Click on ground plane to set center</p>
            <p><strong>Step 2:</strong> Drag to set footprint (lx, lz)</p>
            <p><strong>Step 3:</strong> Adjust dimensions with keyboard:</p>
            <ul style={{ marginLeft: '20px', fontSize: '12px' }}>
              <li><strong>↑/↓</strong> - Adjust height (ly)</li>
              <li><strong>←/→</strong> - Adjust width (lx)</li>
              <li><strong>PgUp/PgDn</strong> - Adjust depth (lz)</li>
              <li><strong>Mouse wheel</strong> - Alternative height control</li>
            </ul>
            <p><strong>Step 4:</strong> Press <strong>Enter</strong> to create, <strong>Esc</strong> to cancel</p>
            <p style={{ fontSize: '11px', color: '#aaa', marginTop: '5px' }}>
              💡 <strong>Laptop tip:</strong> Use arrow keys for precise control instead of mouse wheel
            </p>
            <p style={{ fontSize: '11px', color: '#ff6b35', marginTop: '5px' }}>
              🔍 <strong>Debug:</strong> Mode is active - click on the grid to start creating
            </p>
          </div>
        )}
        <p>Total boxes in state: {boxes.length}</p>
      </div>
      
      <Canvas camera={{ position: [5, 5, 5] }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        
        {/* Grid for reference */}
        <gridHelper args={[10, 10]} />
        
        {/* Axes for reference */}
        <axesHelper args={[5]} />
        
        {/* Your LabelLayer */}
        <LabelLayer 
          currentFrameId={currentFrame}
          boxes={boxes}
          onTransformStart={handleTransformStart}
          onTransformEnd={handleTransformEnd}
          onBoxAction={handleBoxAction}
          mode={mode}
          onModeChange={handleModeChange}
          selectedId={selectedId}
          onSelectionChange={handleSelectionChange}
        />
      </Canvas>

      {/* LabelHUD */}
      <LabelHUD
        selectedId={selectedId}
        boxes={boxes}
        onBoxAction={handleBoxAction}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  )
}

export default LabelLayerTest
